import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useDraft } from '../../state/draft';
import { STARTER_PROMPTS } from '@/webapp/plan/scaffolds';
import { isLeavingForSignIn } from '../../api/oauth';
import { pageIsLeaving } from '../../platform/leaving';
import { funnelStep, useFunnelStep, type FunnelProps } from '../../analytics/funnel';
import { tailorPlan } from '../../state/tailor';
import { ApiError } from '../../api/client';
import type { AppPlan } from '@/webapp/plan/planTypes';
import { Button, Screen } from '../../ui';
import { useSession } from '../../state/useSession';
import { AD_DEMOS, answeredPrompt, buildsFrom, demoForVisit, MAX_ANSWER, SITE_ORIGIN, type AdDemo } from '../../journey/demos';
import { useKeyboardInset } from '../../platform/keyboardInset';
import { variant } from '../../analytics/experiment';
import { reportMakeOneTapped } from '../../state/trial';
import { markAutoBuild } from '../../state/autoBuild';
import { currentVisit, secondsBucket } from '../../analytics/visits';
import { readIntoDraft } from '../../journey/attach';
import { CREATE_ACCEPT } from './createPicks';
import { useThumbnails } from './thumbnails';
import { stashDraft } from '../../state/draftStash';

/** Long enough for any real sentence; short enough that a link can't paste in an essay. */
const MAX_LINKED_IDEA = 1000;

/**
 * The front door, ported from src/app/(onboarding)/create.tsx. One idea in, nothing else.
 *
 * The starters are deliberately specific — "an event app" produces mush, "RSVP for a
 * birthday dinner" produces an app — so tapping one goes straight to a plan.
 */
export default function Idea() {
  const navigate = useNavigate();
  const setDescription = useDraft((s) => s.setDescription);
  const setAppPlan = useDraft((s) => s.setPlan);
  const stored = useDraft((s) => s.description);
  // saddle-sites.com's hero links here with the sentence the visitor already typed. It
  // only fills the field — nothing is planned or spent until they press Continue.
  const [params] = useSearchParams();
  /*
   * Or it arrives in router state: the journey needs an account, so an ad's visitor signs up
   * first, and the sign-in that brings them back hands the idea over there rather than in the
   * address, where the by-then-live pixel would read it (shell/returnTo.ts).
   */
  const location = useLocation();
  const handedOver = (location.state as { idea?: unknown } | null)?.idea;
  const fromLink = (params.get('idea') ?? (typeof handedOver === 'string' ? handedOver : undefined))
    ?.trim()
    .slice(0, MAX_LINKED_IDEA);
  const [idea, setIdea] = useState(fromLink || stored);
  /*
   * Whether they came with their idea already written — an ad, or the landing site's hero. Read
   * once: the effect below takes the sentence out of the address, and the screen must not change
   * its mind about who it is talking to when it does.
   */
  const [linked] = useState(() => Boolean(fromLink));
  useFunnelStep('idea_viewed');


  /*
   * Read once, then taken out of the address bar.
   *
   * The sentence is the person's own words — an address, a child's name — and while it sits
   * in the URL, every PageView the Meta pixel sends carries the full URL with it. The privacy
   * policy says Meta is never sent prompts. Only `idea` goes: `fbclid` and `utm_*` are the
   * ad attribution the pixel exists for, and they stay. The field already holds the text.
   * ConsentBanner also waits for this param to be gone, so neither side relies on the order
   * the two effects run in.
   *
   * The idea handed over in router state is taken out of the history entry the same way. Left
   * there, the browser's Back from the plan put the ad's sentence back in the field over the
   * person's edit, and Continue from there threw the edit away (review, 2026-09-29). The field
   * holds the text; the draft holds it once they continue.
   */
  useEffect(() => {
    if (!params.has('idea') && typeof handedOver !== 'string') return;
    const next = new URLSearchParams(params);
    next.delete('idea');
    const search = next.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '', hash: location.hash },
      { replace: true, state: null },
    );
    // Once, on arrival: the link is read a single time and the field owns the text after.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * The wait for the plan, shown here (2026-10-04). "Plan my app" used to jump to the plan screen at
   * once and let the tailored plan arrive some seconds later with nothing saying it was coming; ad
   * visitors spent 10 s to 2 min on this screen and left. Now the press starts the plan HERE, the
   * idea stays on screen, and each line under it is true when it shows: the idea was sent, the model
   * is reading it, and then what it actually planned. The plan screen opens with the plan ready
   * (state/tailor.ts shares the one request), or — when it fails — with its usual line about why.
   */
  const [waiting, setWaiting] = useState<{ startedAt: number; slow: boolean; plan: AppPlan | null; failed?: boolean } | null>(null);
  const inFlight = useRef<number | null>(null);
  useEffect(() => {
    const onHide = (): void => {
      if (inFlight.current === null) return;
      funnelStep('plan_abandoned', { seconds: planSeconds(Date.now() - inFlight.current) }, { now: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, []);

  const { isAuthenticated } = useSession();
  /*
   * The ad's demo, live, before anything else (2026-10-04, preview-first journey step 1): an ad
   * visitor sees the app the ad promised, made with Saddle, and chooses to make their own. Anyone
   * without a matching demo (the landing site, a direct visit, an unknown ad) starts at the composer.
   */
  const [demo] = useState(() => (linked ? demoForVisit(currentVisit().utm_content, fromLink) : null));
  /*
   * V6's A/B test (analytics/experiment.ts): a visitor shown a demo joins it here, before the first
   * step is sent, so every event of theirs says which group. Somebody with no demo never joins.
   */
  const [group] = useState(() => (demo ? variant('demo_question') : null));
  const [asking, setAsking] = useState(false);
  const [stage, setStage] = useState<'preview' | 'starters' | 'compose' | 'creating'>(() => (demo ? 'preview' : 'compose'));
  const prefilledWith = useRef<string>(fromLink ?? '');
  const attachedMedia = useDraft((s) => s.attachedMedia);
  const photos = useMemo(() => attachedMedia.filter((m) => m.contentType === 'image/jpeg'), [attachedMedia]);
  const thumbUrls = useThumbnails(photos);
  const thumbs = photos.map((p) => ({ name: p.name, url: thumbUrls[p.name] })).filter((t): t is { name: string; url: string } => Boolean(t.url));
  const [attachProblems, setAttachProblems] = useState<string[]>([]);
  const [attaching, setAttaching] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const opened = useRef(Date.now());
  const typed = useRef(false);

  function chooseFromPreview(choice: 'make_own' | 'scratch'): void {
    const seconds = secondsBucket(Date.now() - opened.current);
    funnelStep('preview_choice', { choice, seconds, loaded: demoLoaded() });
    opened.current = Date.now();
    if (choice === 'scratch') {
      // A few starting points first, never a blank box (handoff §2); "Something else…" opens the composer.
      setIdea('');
      prefilledWith.current = '';
      setStage('starters');
      return;
    }
    /*
     * "Make one like this for you" (handoff §2, 2026-10-12): no composer — the ad's idea is planned
     * now, and the plan screen builds it with its default features and look (state/autoBuild.ts).
     */
    funnelStep('demo_cta_pressed', { seconds });
    reportMakeOneTapped();
    // Variant b: one question first (V6); its answer shapes the build, and Skip builds as a does.
    if (group === 'b' && demo) {
      setAsking(true);
      return;
    }
    setStage('compose');
    startFrom(demo ? buildsFrom(demo) : idea);
  }

  /** Variant b's question, answered (`answer`) or skipped (null). */
  function answerQuestion(answer: string | null): void {
    if (!demo) return;
    const said = answer?.trim() ?? '';
    funnelStep('demo_question_answered', { skipped: said ? 'no' : 'yes' });
    setAsking(false);
    setStage('compose');
    startFrom(said ? answeredPrompt(demo, said) : buildsFrom(demo));
  }

  /** Plan this idea and go straight on to building it (the plan screen presses its own Build). */
  function startFrom(sentence: string): void {
    setIdea(sentence);
    prefilledWith.current = sentence;
    markAutoBuild();
    go(sentence);
  }

  function chooseStarter(starter: Starter): void {
    funnelStep('starter_chosen', { starter: starter.key });
    if (!starter.sentence) {
      setStage('compose');
      return;
    }
    setStage('compose');
    startFrom(starter.sentence);
  }
  // Leaving from the preview is a choice too: the ad's app was not enough.
  useEffect(() => {
    // Variant b's question open: the choice was already made (make_own); leaving now is a drop at the question.
    if (stage !== 'preview' || asking) return;
    const onHide = (): void => {
      funnelStep('preview_choice', { choice: 'left', seconds: secondsBucket(Date.now() - opened.current), loaded: demoLoaded() }, { now: true });
    };
    window.addEventListener('pagehide', onHide);
    return () => window.removeEventListener('pagehide', onHide);
  }, [stage, asking]);

  function onType(next: string): void {
    if (!typed.current) {
      typed.current = true;
      const s = (Date.now() - opened.current) / 1000;
      funnelStep('composer_typing_started', {
        prefilled: prefilledWith.current.length > 0,
        seconds_to_first_key: s < 3 ? '<3' : s < 10 ? '3-10' : s < 30 ? '10-30' : '30+',
      });
    }
    setIdea(next);
  }

  async function attach(files: File[]): Promise<void> {
    if (!files.length) return;
    setAttaching(true);
    setAttachProblems([]);
    try {
      const { problems, addedPhotos, docs } = await readIntoDraft(files);
      setAttachProblems(problems);
      if (addedPhotos.length) funnelStep('composer_attach', { kind: 'photo', count: countBucket(addedPhotos.length) });
      if (docs) funnelStep('composer_attach', { kind: 'document', count: countBucket(docs) });
    } finally {
      setAttaching(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  const go = (text: string): void => {
    const goal = text.trim();
    if (!goal || waiting) return;
    const was = prefilledWith.current.trim();
    funnelStep('create_pressed', {
      origin: !was ? 'typed' : goal === was ? 'prefilled_unchanged' : 'prefilled_edited',
      length: goal.length < 40 ? '<40' : goal.length < 120 ? '40-120' : goal.length < 300 ? '120-300' : '300+',
    });
    setDescription(goal);
    // Cleared so the plan screen always starts from a scaffold built for THIS idea,
    // never a plan left over from a previous run.
    setAppPlan(null);
    const startedAt = Date.now();
    inFlight.current = startedAt;
    funnelStep('plan_requested', { auto: false });
    setWaiting({ startedAt, slow: false, plan: null });
    /*
     * Signed out, the account is asked for NOW, over the loading view, so the plan's wait happens
     * while they type their email. The draft (idea and attachments) is put away first: the code
     * arrives by email, and a phone can drop the page on the way back.
     */
    if (!isAuthenticated) {
      setStage('creating');
      void stashDraft('').catch(() => undefined);
    }
    const slowLine = setTimeout(() => setWaiting((w) => (w && !w.plan ? { ...w, slow: true } : w)), 8000);
    tailorPlan(goal).then(
      (result) => {
        clearTimeout(slowLine);
        inFlight.current = null;
        funnelStep('plan_ready', { seconds: planSeconds(Date.now() - startedAt) });
        setWaiting((w) => (w ? { ...w, plan: result?.plan ?? null } : w));
        // Signed in: long enough to read what was planned, then the plan. Signed out: the card stays.
        // Long enough to read what was planned, then the plan — signed in or not.
        setTimeout(() => navigate('/new/plan'), result?.plan ? 1200 : 0);
      },
      (err: unknown) => {
        clearTimeout(slowLine);
        inFlight.current = null;
        /*
         * Cut off because this page is leaving for Apple or Google (2026-10-06: a visitor's plan
         * "failed" in the very second they tapped Apple). The plan is tailored again on the way back;
         * nothing failed, so nothing is logged or said.
         */
        if (isLeavingForSignIn()) return;
        // Cut off by this page reloading or closing (2026-10-09): the plan did not fail, the page left.
        if (pageIsLeaving()) return;
        funnelStep('plan_failed', { reason: failureReason(err) });
        setWaiting((w) => (w ? { ...w, failed: true } : w));
        navigate('/new/plan');
      },
    );
  };
  // Read when the plan lands, not when it was asked for: they may have signed in meanwhile.
  const signedInNow = useRef(isAuthenticated);
  signedInNow.current = isAuthenticated;

  if (stage === 'preview' && demo) {
    return <DemoPreview demo={demo} onChoose={chooseFromPreview} asking={asking} onAnswer={answerQuestion} />;
  }

  if (stage === 'starters') {
    return (
      <Screen>
        {/* The way back to the ad's demo they came from (V6 polish): "Start from scratch" was one-way. */}
        {demo && (
          <button
            type="button"
            className="link-button starters-back"
            onClick={() => {
              opened.current = Date.now();
              setStage('preview');
            }}
          >
            ‹ Back to the example
          </button>
        )}
        <h1 className="type-display screen-title">What would you like to make?</h1>
        <div className="starters">
          {STARTERS.map((st) => (
            <button key={st.key} type="button" className="starter-card" onClick={() => chooseStarter(st)}>
              <span className="type-body starter-title">{st.title}</span>
              {st.line && <span className="type-caption starter-line">{st.line}</span>}
            </button>
          ))}
        </div>
      </Screen>
    );
  }

  if (stage === 'creating') {
    return (
      <Screen>
        {/*
          No account asked for here any more (the owner's flow, 2026-10-08): the plan, its features and
          its look are theirs to walk signed out, and the account is asked for while the site renders.
        */}
        <CreatingView idea={idea} thumbs={thumbs} waiting={waiting} />
      </Screen>
    );
  }

  return (
    <Screen title="What do you want to make?">
      <p className="type-body idea-sub">
        {linked ? 'Change anything you like, add your photos, then tap Create.' : 'Describe it in a line. Add photos or a menu with +.'}
      </p>
      <div className="composer">
        {thumbs.length > 0 && (
          <ul className="composer-thumbs" aria-label="Attached photos">
            {thumbs.map((t) => <li key={t.name}><img src={t.url} alt="" /></li>)}
          </ul>
        )}
        <label className="visually-hidden" htmlFor="idea">Describe the app you want</label>
        <textarea
          id="idea"
          className="field idea-input composer-input"
          value={idea}
          rows={4}
          readOnly={waiting !== null}
          onChange={(e) => onType(e.target.value)}
          placeholder="Describe what you want to make…"
        />
        <div className="composer-bar">
          <input
            ref={fileInput}
            type="file"
            multiple
            accept={CREATE_ACCEPT}
            className="visually-hidden"
            aria-label="Photos or documents to add"
            onChange={(e) => void attach(Array.from(e.target.files ?? []))}
          />
          <button
            type="button"
            className="composer-plus"
            aria-label="Add photos or documents"
            disabled={attaching || waiting !== null}
            onClick={() => fileInput.current?.click()}
          >
            +
          </button>
          <span className="type-caption composer-hint">
            {attaching ? 'Reading your files…' : attachedMedia.length ? `${attachedMedia.length} photo${attachedMedia.length === 1 ? '' : 's'} added` : 'Photos, a menu, a schedule'}
          </span>
        </div>
      </div>
      {attachProblems.length > 0 && (
        <div role="alert" className="type-caption field-error-text">
          {attachProblems.map((p) => <p key={p}>{p}</p>)}
        </div>
      )}
      <Button
        title={waiting ? 'Planning your app…' : 'Create'}
        loading={waiting !== null && !waiting.plan}
        onPress={() => go(idea)}
        disabled={idea.trim().length === 0 || waiting !== null || attaching}
      />
      {waiting && <PlanProgress waiting={waiting} />}

      {/* Somebody who arrived with their own idea is not offered eight others. */}
      {!linked && !waiting && (
        <>
          <h2 className="type-micro section-label">Or start from one of these</h2>
          <div className="starter-grid">
            {STARTER_PROMPTS.map((s) => (
              <button
                key={s.label}
                type="button"
                className="card clay-card pressable starter"
                aria-label={`${s.label}. ${s.prompt}`}
                onClick={() => go(s.prompt)}
              >
                <span className="type-heading">{s.label}</span>
                <span className="type-caption starter-sub">{s.prompt}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </Screen>
  );
}

/** The wait as the plan's own buckets (analytics/funnel.ts). */
function planSeconds(ms: number): NonNullable<FunnelProps['seconds']> {
  const s = ms / 1000;
  return s < 5 ? '<5' : s < 10 ? '5-10' : s < 20 ? '10-20' : '20+';
}

/** Why a plan did not come: the day's free plans, the clock, or anything else. */
function failureReason(err: unknown): NonNullable<FunnelProps['reason']> {
  if (err instanceof ApiError) {
    if (err.code === 'anon_plan_limit' || err.code === 'subscription_required' || err.kind === 'out_of_credits' || err.kind === 'rate_limited') return 'cap';
    if (err.kind === 'timeout') return 'timeout';
  }
  return 'error';
}

/**
 * What is happening, in lines that are each true when they appear: the idea went, the model is
 * reading it (with the honest "about 10 seconds" once it has taken a while), and then what it planned.
 */
function PlanProgress({ waiting }: { waiting: { slow: boolean; plan: AppPlan | null } }) {
  const included = waiting.plan?.features.filter((f) => f.included) ?? [];
  return (
    <ul className="plan-progress" role="status" aria-live="polite">
      <li className="plan-progress-done">Got your idea</li>
      {waiting.plan ? (
        <>
          <li className="plan-progress-done">Read it and chose what your app needs</li>
          <li className="plan-progress-done">
            Planned {included.length} feature{included.length === 1 ? '' : 's'}
            {included.length ? `: ${included.slice(0, 3).map((f) => f.label).join(', ')}${included.length > 3 ? '…' : ''}` : ''}
          </li>
          <li className="plan-progress-now">Opening your plan…</li>
        </>
      ) : (
        <>
          <li className="plan-progress-now">Reading it and choosing the pages and features…</li>
          {waiting.slow && <li className="plan-progress-note">Plans usually take about 10 seconds.</li>}
        </>
      )}
    </ul>
  );
}

function countBucket(n: number): NonNullable<FunnelProps['count']> {
  return n <= 1 ? '1' : n <= 5 ? '2-5' : '6+';
}

/**
 * "Start from scratch" offers these first (trial14-and-demo handoff §2): the ads' own ideas, each a
 * tap from building, and "Something else…" for the composer. No blank box first.
 */
interface Starter {
  key: 'golf' | 'trip' | 'potluck' | 'wedding' | 'volunteer' | 'cafe' | 'other';
  title: string;
  line?: string;
  sentence?: string;
}
const sentenceOf = (ad: string): string | undefined => {
  const demo = AD_DEMOS.find((d) => d.ad === ad);
  return demo ? buildsFrom(demo) : undefined;
};
const STARTERS: Starter[] = [
  { key: 'golf', title: 'Golf tracker', line: 'Every round, every club, with charts', sentence: sentenceOf('Golf') },
  { key: 'trip', title: 'Trip plan', line: 'The days, the stays, everyone’s flights', sentence: sentenceOf('Trip') },
  { key: 'potluck', title: 'Potluck sign-up', line: 'Who brings what, so it all gets covered', sentence: sentenceOf('Potluck') },
  { key: 'wedding', title: 'Wedding RSVP', line: 'Your weekend, and every guest’s reply', sentence: sentenceOf('Wedding') },
  { key: 'volunteer', title: 'Volunteer schedule', line: 'Shifts people sign up for, closed when full', sentence: sentenceOf('Volunteer') },
  { key: 'cafe', title: 'Café schedule', line: 'The week’s shifts, picked by your staff', sentence: sentenceOf('Schedule') },
  { key: 'other', title: 'Something else…', line: 'Describe it in your own words' },
];

/**
 * The ad's app, live and full screen — the whole website, not a phone frame (owner, 2026-10-08) —
 * with "Make one like this for you" over it from the first second (trial14-and-demo handoff §2,
 * 2026-10-12: about 60% of ad visitors looked for 20-55 s and left without choosing, while the old
 * card waited 15 s to appear). One true line under it, and "Start from scratch" as a quieter link.
 * The demo is only SHOWN: framed read-only from its own site, never copied, changed or republished.
 */
/** Whether the demo frame on screen has loaded (read when the choice is made). */
function demoLoaded(): boolean {
  return document.querySelector('.demo-full-loaded') !== null;
}

/** How long the "Try it" hint stays over the demo once it has loaded (V6). */
export const TRY_IT_MS = 5000;

function DemoPreview({ demo, onChoose, asking, onAnswer }: {
  demo: AdDemo;
  onChoose: (c: 'make_own' | 'scratch') => void;
  /** Variant b, after the first tap: the one question is open over the bottom of the demo. */
  asking: boolean;
  onAnswer: (answer: string | null) => void;
}) {
  useFunnelStep('preview_viewed', true, { demo: demo.slug, ref: currentVisit().ref });
  useFunnelStep('demo_cta_shown', true, { demo: demo.slug });
  /*
   * How long the live demo took to appear (handoff follow-up, 2026-10-05): 10 of the first 15
   * visitors inside Facebook's browser stopped on this screen, and a slow frame there looks exactly
   * like a lack of interest. `preview_loaded` says how long; the choice says whether it had loaded.
   */
  const opened = useRef(Date.now());
  const [loaded, setLoaded] = useState(false);
  // V6: a still of the demo covers the frame until it has loaded. A missing picture just goes.
  const [posterBroken, setPosterBroken] = useState(false);
  // V6: "Try it: …" for a few seconds once the live demo is there to try.
  const [hint, setHint] = useState<'waiting' | 'showing' | 'gone'>('waiting');
  useEffect(() => {
    if (hint !== 'showing') return;
    const timer = setTimeout(() => setHint('gone'), TRY_IT_MS);
    /*
     * A tap inside the demo cannot be seen (it is another origin), but the window loses focus to the
     * frame when it is tapped: that is the visitor trying it, and the hint has done its job.
     */
    const tried = (): void => setHint('gone');
    window.addEventListener('blur', tried);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('blur', tried);
    };
  }, [hint]);
  const onLoad = (): void => {
    if (loaded) return;
    setLoaded(true);
    setHint((h) => (h === 'waiting' ? 'showing' : h));
    funnelStep('preview_loaded', { demo: demo.slug, seconds: secondsBucket(Date.now() - opened.current) });
  };
  return (
    <div className={loaded ? 'demo-full demo-full-loaded' : 'demo-full'}>
      {/*
        The demo's own stage, above the button rather than under it (V6 polish, 2026-10-14): the golf
        and trip demos keep their tab bars at the bottom, and the hint asks for "Stats at the bottom".
      */}
      <div className="demo-stage">
        {/* Until the live app arrives: a still frame, so the screen never reads as broken. */}
        {!loaded && (
          <div className="demo-loading" role="status">
            <span className="demo-loading-bar" /><span className="demo-loading-bar" /><span className="demo-loading-bar demo-loading-short" />
            <span className="visually-hidden">Loading the live app…</span>
          </div>
        )}
        <iframe
          title="The app this ad shows, live"
          src={`${SITE_ORIGIN}/site/${demo.slug}`}
          loading="eager"
          referrerPolicy="no-referrer"
          onLoad={onLoad}
        />
        {!posterBroken && (
          <img
            className={loaded ? 'demo-poster demo-poster-gone' : 'demo-poster'}
            src={demo.poster}
            alt=""
            aria-hidden="true"
            onError={() => setPosterBroken(true)}
          />
        )}
      </div>
      <div className={`demo-hint-region demo-hint-region-${demo.tryItAt ?? 'top'}`} aria-live="polite">
        {hint === 'showing' && !asking && (
          <p className="demo-hint type-body">
            <span>{demo.tryIt}</span>
            <button type="button" className="demo-hint-close" aria-label="Dismiss the hint" onClick={() => setHint('gone')}>×</button>
          </p>
        )}
      </div>
      {asking ? (
        <DemoQuestion demo={demo} onAnswer={onAnswer} />
      ) : (
        <section className="demo-cta" aria-label="Make your own">
          <Button title={demo.cta || 'Make one like this for you'} onPress={() => onChoose('make_own')} />
          <p className="type-caption demo-cta-line">Free to start. No card needed.</p>
          <button type="button" className="link-button demo-scratch" onClick={() => onChoose('scratch')}>
            Start from scratch
          </button>
        </section>
      )}
    </div>
  );
}

/**
 * Variant b's one question (V6), over the bottom of the demo where the button was: their answer goes
 * into the demo's own prompt in place of its specifics ("Mia and Theo" becomes theirs).
 */
function DemoQuestion({ demo, onAnswer }: { demo: AdDemo; onAnswer: (answer: string | null) => void }) {
  useFunnelStep('demo_question_shown', true, { demo: demo.slug });
  const [answer, setAnswer] = useState('');
  const fieldId = useId();
  // Lifted above the phone's keyboard while it is up, so Build it and Skip stay in sight.
  const lift = useKeyboardInset();
  const sheet = useRef<HTMLElement>(null);
  return (
    <section ref={sheet} className="demo-cta demo-question" aria-label="One question" style={lift ? { bottom: `${lift}px` } : undefined}>
      <form
        className="demo-question-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (answer.trim()) onAnswer(answer);
        }}
      >
        <label className="type-heading demo-question-ask" htmlFor={fieldId}>{demo.question.ask}</label>
        <input
          id={fieldId}
          className="field demo-question-field"
          value={answer}
          maxLength={MAX_ANSWER}
          // An example, said as one, so it never reads as an answer already given.
          placeholder={`e.g. ${demo.question.placeholder}`}
          enterKeyHint="go"
          autoComplete="off"
          // The one thing on screen to do, right after the tap that asked for it.
          autoFocus
          onChange={(e) => setAnswer(e.target.value)}
          // Where the browser has no visualViewport to lift by: at least bring the sheet into view.
          onFocus={() => {
            if (!window.visualViewport) sheet.current?.scrollIntoView?.({ block: 'end' });
          }}
        />
        <Button title="Build it" type="submit" disabled={!answer.trim()} />
        <button type="button" className="link-button demo-scratch" onClick={() => onAnswer(null)}>
          Skip
        </button>
        <p className="type-caption demo-cta-line">Free to start. No card needed.</p>
      </form>
    </section>
  );
}

/**
 * The wait after Create (step 3): their idea quoted, their photos, a phone filling in, and steps
 * that are each true when they show.
 */
function CreatingView({ idea, thumbs, waiting }: {
  idea: string;
  thumbs: { name: string; url: string }[];
  waiting: { slow: boolean; plan: AppPlan | null; failed?: boolean } | null;
}) {
  const included = waiting?.plan?.features.filter((f) => f.included) ?? [];
  const planned = Boolean(waiting?.plan) || Boolean(waiting?.failed);
  return (
    <div className="creating">
      <blockquote className="creating-idea">{idea}</blockquote>
      <div className={planned ? 'creating-phone creating-phone-ready' : 'creating-phone'} aria-hidden="true">
        <span /><span /><span /><span />
        {thumbs.slice(0, 3).map((t) => <img key={t.name} src={t.url} alt="" />)}
      </div>
      <ul className="plan-progress" role="status" aria-live="polite">
        <li className="plan-progress-done">Got your idea</li>
        {thumbs.length > 0 && <li className="plan-progress-done">{thumbs.length} photo{thumbs.length === 1 ? '' : 's'} ready to place</li>}
        {waiting?.plan ? (
          <li className="plan-progress-done">
            Planned {included.length} feature{included.length === 1 ? '' : 's'}
            {included.length ? `: ${included.slice(0, 3).map((f) => f.label).join(', ')}${included.length > 3 ? '…' : ''}` : ''}
          </li>
        ) : waiting?.failed ? (
          <li className="plan-progress-done">Your plan is ready to finish once you're in</li>
        ) : (
          <li className="plan-progress-now">Reading your idea and choosing the pages…</li>
        )}
      </ul>
    </div>
  );
}
